import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaTachometerAlt,
  FaUsers,
  FaChartBar,
  FaExchangeAlt,
  FaArrowDown,
  FaArrowUp,
  FaChartLine,
  FaWallet,
  FaTicketAlt,
  FaTimes,
  FaChevronDown,
  FaBroadcastTower,
  FaListAlt,
  FaCog,
  FaStore,
  FaHistory,
  FaHandHoldingUsd,
  FaReceipt,
  FaPeopleArrows,
  FaRandom,
  FaMoneyCheckAlt,
  FaGift,
  FaUniversity,
  FaCrown,
} from "react-icons/fa";

const RESTRICTED_TRADE_LIST_USERNAMES = ["mking", "rking", "aking"];

const links = [
  { to: "/", label: "Dashboard", icon: <FaTachometerAlt /> },
  { to: "/users", label: "Users", icon: <FaUsers /> },
  { to: "/exchangers", label: "Exchangers", icon: <FaStore /> },
  {
    label: "Statistics",
    icon: <FaChartBar />,
    children: [
      { to: "/stats-trade", label: "Trade", icon: <FaChartLine /> },
      { to: "/stats-transaction", label: "Transaction", icon: <FaExchangeAlt /> },
    ],
  },
  {
    label: "Transactions",
    icon: <FaReceipt />,
    children: [
      { to: "/deposits", label: "Deposits", icon: <FaArrowDown /> },
      { to: "/p2p-transfer-list", label: "P2P Transfers", icon: <FaPeopleArrows /> },
      { to: "/p2p-withdrawal-list", label: "P2P Withdrawals", icon: <FaUniversity /> },
      { to: "/withdrawals", label: "Withdrawals", icon: <FaArrowUp /> },
    ],
  },
  {
    label: "History",
    icon: <FaHistory />,
    children: [
      { to: "/swap-history", label: "Swap History", icon: <FaRandom /> },
      { to: "/p2p-report", label: "P2P History", icon: <FaPeopleArrows /> },
      { to: "/recovery-trade-history", label: "Recovery History", icon: <FaHandHoldingUsd /> },
    ],
  },
  {
    label: "Trade",
    icon: <FaChartLine />,
    children: [
      { to: "/trade-list", label: "Trade List", icon: <FaListAlt /> },
      { to: "/trade-history", label: "Trade History", icon: <FaHistory /> },
      { to: "/live-trades-all", label: "Live Trades", icon: <FaBroadcastTower /> },
    ],
  },
  {
    label: "Payouts",
    icon: <FaWallet />,
    children: [
      { to: "/coupon", label: "Coupon", icon: <FaTicketAlt /> },
      { to: "/salary", label: "Salary", icon: <FaMoneyCheckAlt /> },
      { to: "/bonus", label: "Bonus", icon: <FaGift /> },
      { to: "/leader-list", label: "Leader List", icon: <FaCrown /> },
      { to: "/recovery-trade", label: "Recovery", icon: <FaHandHoldingUsd /> },
    ],
  },
  { to: "/settings", label: "Settings", icon: <FaCog /> },
];

function SidebarContent({ collapsed, onNavigate, showBrand = false }) {
  const location = useLocation();
  const rootRef = useRef(null);
  const hoverCloseTimerRef = useRef(null);

  const visibleLinks = useMemo(() => {
    const username = (localStorage.getItem("iutyeurds") || "").trim().toLowerCase();
    const isRestricted = RESTRICTED_TRADE_LIST_USERNAMES.includes(username);
    if (!isRestricted) return links;
    return links
      .map((l) => {
        if (l.label === "Trade" && l.children?.length) {
          const children = l.children.filter((c) => c.to !== "/trade-list");
          return { ...l, children };
        }
        return l;
      })
      .filter((l) => {
        if (l.to === "/settings") return false;
        return !l.children || l.children.length > 0;
      });
  }, []);

  const menuLabels = useMemo(
    () => visibleLinks.filter((l) => l.children?.length).map((l) => l.label),
    [visibleLinks]
  );

  const getActiveMenusFromRoute = () => {
    const next = {};
    menuLabels.forEach((label) => (next[label] = false));
    visibleLinks.forEach((l) => {
      if (l.children?.length) {
        next[l.label] = l.children.some((c) => location.pathname === c.to);
      }
    });
    return next;
  };

  const [openMenus, setOpenMenus] = useState(() => getActiveMenusFromRoute());

  useEffect(() => {
    setOpenMenus(getActiveMenusFromRoute());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const [hoverMenu, setHoverMenu] = useState(null);

  const clearHoverCloseTimer = () => {
    if (hoverCloseTimerRef.current) {
      clearTimeout(hoverCloseTimerRef.current);
      hoverCloseTimerRef.current = null;
    }
  };

  const scheduleHoverClose = () => {
    clearHoverCloseTimer();
    hoverCloseTimerRef.current = setTimeout(() => setHoverMenu(null), 120);
  };

  const handleNavigate = () => {
    setHoverMenu(null);
    clearHoverCloseTimer();
    if (typeof onNavigate === "function") onNavigate();
  };

  const toggleMenu = (label) => {
    setHoverMenu(null);
    setOpenMenus((prev) => {
      const next = {};
      menuLabels.forEach((k) => (next[k] = false));
      next[label] = !prev[label];
      return next;
    });
  };

  const openHoverMenu = (e, label, children) => {
    if (!collapsed) return;
    const itemRect = e.currentTarget.getBoundingClientRect();
    const rootRect = rootRef.current?.getBoundingClientRect();
    const left = (rootRect?.right ?? itemRect.right) + 8;

    clearHoverCloseTimer();
    setHoverMenu({
      label,
      top: Math.max(8, itemRect.top),
      left,
      children,
    });
  };

  return (
    <div className="tb-sidebar-inner" ref={rootRef}>
      {showBrand && (
        <div className={`tb-sidebar-brand ${collapsed ? "justify-content-center px-2" : ""}`}>
          <span className="tb-brand-mark">TB</span>
          {!collapsed && (
            <div>
              <div className="tb-sidebar-brand-title">TradeBid</div>
              <div className="tb-sidebar-brand-sub">Admin Panel</div>
            </div>
          )}
        </div>
      )}

      {!collapsed && <div className="tb-nav-section">Menu</div>}

      <nav className="tb-sidebar-nav">
        {visibleLinks.map((l) => {
          if (l.children?.length) {
            const anyChildActive = l.children.some((c) => location.pathname === c.to);
            const isOpen = !!openMenus[l.label];

            return (
              <div key={l.label} className="mb-1">
                <button
                  type="button"
                  onClick={() => (!collapsed ? toggleMenu(l.label) : undefined)}
                  onMouseEnter={(e) => openHoverMenu(e, l.label, l.children)}
                  onMouseLeave={() => (collapsed ? scheduleHoverClose() : undefined)}
                  className={[
                    "tb-nav-link w-100 d-flex align-items-center text-decoration-none border-0",
                    collapsed ? "justify-content-center" : "gap-2",
                    anyChildActive ? "is-active" : "",
                  ].join(" ")}
                  style={{ background: "transparent" }}
                  title={collapsed ? l.label : undefined}
                >
                  <span className="tb-nav-ico">{l.icon}</span>
                  {!collapsed && (
                    <>
                      <span className="flex-grow-1 text-start">{l.label}</span>
                      <motion.span
                        animate={{ rotate: isOpen ? 180 : 0 }}
                        transition={{ duration: 0.15 }}
                        style={{ opacity: 0.65, fontSize: "0.65rem" }}
                      >
                        <FaChevronDown />
                      </motion.span>
                    </>
                  )}
                </button>

                <AnimatePresence initial={false}>
                  {!collapsed && isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.18 }}
                      className="ps-2 mt-1 overflow-hidden"
                    >
                      {l.children.map((c) => (
                        <NavLink
                          key={c.to}
                          to={c.to}
                          onClick={handleNavigate}
                          className={({ isActive }) =>
                            [
                              "tb-nav-link d-flex align-items-center text-decoration-none mb-1 gap-2",
                              isActive ? "active" : "",
                            ].join(" ")
                          }
                        >
                          <span className="tb-nav-ico" style={{ fontSize: "0.78rem" }}>
                            {c.icon}
                          </span>
                          <span style={{ fontSize: "0.82rem" }}>{c.label}</span>
                        </NavLink>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          }

          return (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              onClick={handleNavigate}
              title={collapsed ? l.label : undefined}
              className={({ isActive }) =>
                [
                  "tb-nav-link d-flex align-items-center text-decoration-none mb-1",
                  collapsed ? "justify-content-center" : "gap-2",
                  isActive ? "active" : "",
                ].join(" ")
              }
            >
              <span className="tb-nav-ico">{l.icon}</span>
              {!collapsed && <span>{l.label}</span>}
            </NavLink>
          );
        })}
      </nav>

      <div className="tb-sidebar-footer">
        {!collapsed ? <>© {new Date().getFullYear()} TradeBid</> : <span title="©">©</span>}
      </div>

      {collapsed && hoverMenu?.children?.length ? (
        <div
          className="sidebar-hover-menu position-fixed"
          style={{ top: hoverMenu.top, left: hoverMenu.left, zIndex: 2000 }}
          onMouseEnter={() => clearHoverCloseTimer()}
          onMouseLeave={() => scheduleHoverClose()}
        >
          <div className="sidebar-hover-header">{hoverMenu.label}</div>
          <div className="p-1">
            {hoverMenu.children.map((c) => (
              <NavLink
                key={c.to}
                to={c.to}
                onClick={handleNavigate}
                className={({ isActive }) =>
                  [
                    "tb-nav-link d-flex align-items-center text-decoration-none mb-1 gap-2",
                    isActive ? "active" : "",
                  ].join(" ")
                }
              >
                <span className="tb-nav-ico">{c.icon}</span>
                <span>{c.label}</span>
              </NavLink>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function Sidebar({ variant, open, onClose, collapsed = false }) {
  if (variant === "desktop") {
    const expandedWidth = 232;
    const collapsedWidth = 68;

    return (
      <motion.aside
        className="tb-sidebar"
        animate={{ width: collapsed ? collapsedWidth : expandedWidth }}
        transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
      >
        <SidebarContent collapsed={collapsed} showBrand />
      </motion.aside>
    );
  }

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            className="position-fixed top-0 start-0 w-100 h-100 tb-drawer-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
        )}
      </AnimatePresence>

      <motion.div
        className="position-fixed top-0 start-0 h-100 tb-mobile-drawer d-flex flex-column"
        style={{ width: 260 }}
        initial={false}
        animate={{ x: open ? 0 : -280 }}
        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom flex-shrink-0">
          <div className="d-flex align-items-center gap-2">
            <span className="tb-brand-mark">TB</span>
            <span style={{ fontWeight: 600, color: "var(--tb-ink)", fontSize: "0.9rem" }}>Menu</span>
          </div>
          <button className="tb-icon-btn" onClick={onClose} type="button" aria-label="Close menu">
            <FaTimes />
          </button>
        </div>

        <div className="flex-grow-1 min-h-0 d-flex flex-column" style={{ minHeight: 0 }}>
          <SidebarContent collapsed={false} onNavigate={onClose} />
        </div>
      </motion.div>
    </>
  );
}
